"use client"

import React, { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"

interface CommentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (comment: string) => Promise<void>
  isSubmitting?: boolean
  title: string
  description: string
  label?: string
  placeholder?: string
}

const commentFormSchema = z.object({
  comment: z.string().min(1, "Please provide a comment.").refine(
    (val) => val.trim().length > 0,
    "Please provide a comment."
  )
})

type CommentFormData = z.infer<typeof commentFormSchema>

export function CommentDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting = false,
  title,
  description,
  label = "Comment",
  placeholder = "Enter your comment..."
}: CommentDialogProps) {
  const form = useForm<CommentFormData>({
    resolver: zodResolver(commentFormSchema),
    mode: "onChange",
    defaultValues: {
      comment: ""
    }
  })

  useEffect(() => {
    if (open) {
      form.reset({ comment: "" })
    }
  }, [open, form])

  const onSubmit = async (data: CommentFormData) => {
    try {
      await onConfirm(data.comment.trim())
    } catch (err) {
      // Parent handles error display
    }
  }

  const handleCancel = () => {
    form.reset({ comment: "" })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>
                {description}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <FormField
                control={form.control}
                name="comment"
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {label}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={placeholder}
                        className="min-h-[120px]"
                        disabled={isSubmitting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Confirm"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

